import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'

import { PageHeader } from '@ems/ui/patterns/page-header'

import { ServiceOrderSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'
import { NewOrderForm } from '#admin/features/exhibitor/new-order-form.tsx'
import {
  applicationsQuery,
  serviceItemsQuery,
  servicePolicyQuery,
} from '#admin/features/exhibitor/queries.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/**
 * `/exhibition/service-orders/new` — the operator takes a service order for an exhibitor.
 * `?applicationId=` preselects the exhibitor, as the exhibitor page's link does.
 */
export const Route = createFileRoute('/_authenticated/exhibition/service-orders/new')({
  validateSearch: (search: Record<string, unknown>): { applicationId?: string } =>
    typeof search['applicationId'] === 'string' ? { applicationId: search['applicationId'] } : {},
  pendingComponent: ServiceOrderSkeleton,
  staticData: { crumb: 'New order' },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(applicationsQuery()),
      context.queryClient.ensureQueryData(serviceItemsQuery()),
      context.queryClient.ensureQueryData(servicePolicyQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ]),
  component: NewOrderPage,
})

function NewOrderPage() {
  const { applicationId } = Route.useSearch()
  const navigate = useNavigate()
  const { data: applications } = useSuspenseQuery(applicationsQuery())
  const { data: catalog } = useSuspenseQuery(serviceItemsQuery())
  const { data: policy } = useSuspenseQuery(servicePolicyQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())

  // The statuses in which the server takes an order (`create/service.ts`).
  const orderable = applications.items
    .filter((a) => a.status === 'approved' || a.status === 'payment_completed')
    .toSorted((a, b) => a.companyName.localeCompare(b.companyName))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="New service order"
        description="Take an order for an exhibitor, for example at the service desk or by phone."
      />
      <NewOrderForm
        applications={orderable}
        catalog={catalog.items}
        policy={policy}
        zone={manifest?.tenant.timezone ?? 'UTC'}
        initialApplicationId={applicationId}
        onCreated={(id) =>
          void navigate({
            to: '/exhibition/service-orders/$serviceOrderId',
            params: { serviceOrderId: id },
          })
        }
      />
    </div>
  )
}
